import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { token, ready } = useAuth(); // <-- needs ready from context
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!ready) return;              // wait for hydration
    if (!token) navigate('/login', { replace: true });
  }, [ready, token, navigate]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  // Show immediate feedback while the saved session is being restored.
  if (!ready) {
    return (
      <div
        className="flex h-screen w-screen items-center justify-center bg-gray-50"
        role="status"
        aria-live="polite"
        aria-label="Loading application"
      >
        <div className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white px-8 py-6 shadow-lg">
          <svg
            className="h-12 w-12 animate-spin text-[#ac120c]"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle className="opacity-20" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="4" />
            <path fill="currentColor" d="M12 3a9 9 0 0 1 9 9h-4a5 5 0 0 0-5-5V3Z" />
          </svg>
          <p className="text-sm font-semibold text-gray-700">Loading...</p>
        </div>
      </div>
    );
  }

  // Avoid flashing protected content immediately before redirecting to login.
  if (!token) return null;

  return (
    <div className="relative flex h-screen w-screen overflow-hidden">
      {mobileMenuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          aria-label="Close menu"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 md:static md:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <Sidebar onNavigate={() => setMobileMenuOpen(false)} />
      </aside>

      <div className="relative flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 md:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="rounded-md border border-gray-300 p-2"
            aria-label="Toggle menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <span className="text-sm font-semibold">Gym System</span>
        </div>

        <main className="w-full flex-grow overflow-auto p-3 md:p-4">
          {children}
        </main>
      </div>
    </div>
  );
}
